import * as React from "react";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { EventDetailRace } from "../../../hooks/event";
import { Query } from "../../../utils/routing/routing";
import { resultsRoute } from "../../../routes/org";
import { useTranslation } from "../../../utils/i18n/lang";
import { raceMessages, sportLabels } from "../../../domain/event";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Typography,
} from "@mui/material";
import { constNull, pipe } from "fp-ts/function";
import { array, nonEmptyArray, option } from "fp-ts";
import {
    Distance,
    distanceAdd,
    validIsoDateStringFromDateCodec,
} from "../../../domain/common";
import { LocalDistance } from "../../common/LocalDistance";
import { dateTimeFormat, LocalDate } from "../../common/date/LocalDate";
import { InternalLink } from "../../common/Link";
import { categoryLabel, categoryMessages } from "../../../domain/category";
import {
    SegmentsMapDialog,
    ShowSegmentsOnMapBtn,
    useSegmentsToPreviewState,
} from "./ShowSegmentsOnMapBtn";
import { sequenceS } from "fp-ts/Apply";
import { LocalCurrency } from "../../common/LocalCurrency";

interface RaceListProps {
    races: NonEmptyArray<EventDetailRace>;
    resultsLinkProps?: Query<typeof resultsRoute>;
}
export const RaceList = (p: RaceListProps) => {
    const sportT = useTranslation(sportLabels);
    const raceT = useTranslation(raceMessages);
    const categoryM = useTranslation(categoryMessages);
    const segmentsToPreviewState = useSegmentsToPreviewState();

    return (
        <>
            <Typography variant={"h5"} component={"h2"} sx={{ mt: 4, mb: 2 }}>
                Závody
            </Typography>

            {p.races.map((race) => (
                <Card key={race.id} sx={{ mb: 2 }}>
                    <CardContent>
                        <Grid
                            container
                            justifyContent={"space-between"}
                            sx={{ mb: 2 }}
                        >
                            <Grid item>
                                <Typography variant="h6" component="div">
                                    {race.title}
                                </Typography>
                            </Grid>
                            <Grid item>
                                {pipe(
                                    p.resultsLinkProps,
                                    option.fromNullable,
                                    option.fold(
                                        constNull,
                                        (resultsLinkProps) => (
                                            <InternalLink
                                                href={resultsRoute.route({
                                                    ...resultsLinkProps,
                                                    raceId: race.id,
                                                })}
                                            >
                                                <Button>výsledky</Button>
                                            </InternalLink>
                                        )
                                    )
                                )}
                            </Grid>
                        </Grid>
                        {race.start_time == null ? null : (
                            <Typography gutterBottom>
                                {raceT.start_time}
                                {": "}
                                <b>
                                    <LocalDate
                                        date={race.start_time}
                                        format={dateTimeFormat}
                                    />
                                </b>
                            </Typography>
                        )}
                        <Typography gutterBottom>
                            {raceT.sport}
                            {": "}
                            {sportT[race.sport]}
                        </Typography>
                        <Typography gutterBottom>
                            {raceT.course}
                            {": "}
                            {pipe(
                                race.race_segment,
                                array.map((s) => s.segment.distance),
                                array.reduce(0 as Distance, distanceAdd),
                                (d) => (
                                    <LocalDistance distance={d} />
                                )
                            )}{" "}
                            <ShowSegmentsOnMapBtn
                                segments={pipe(
                                    race.race_segment,
                                    array.map((s) => s.segment)
                                )}
                                state={segmentsToPreviewState}
                            />
                        </Typography>

                        {pipe(
                            race.race_category,
                            nonEmptyArray.fromArray,
                            option.fold(constNull, (race_category) => (
                                <>
                                    <Typography>{raceT.categories}:</Typography>
                                    <ul>
                                        {race_category.map((c) => (
                                            <li key={c.category_id}>
                                                {categoryLabel(
                                                    c.category,
                                                    categoryM,
                                                    pipe(
                                                        race.start_time,
                                                        option.fromNullable,
                                                        option.map(
                                                            (startDate) =>
                                                                validIsoDateStringFromDateCodec
                                                                    .encode(
                                                                        startDate
                                                                    )
                                                                    .getFullYear()
                                                        ),
                                                        option.toUndefined
                                                    )
                                                )}
                                                {pipe(
                                                    {
                                                        fee: option.fromNullable(
                                                            c.entry_fee
                                                        ),
                                                        currency:
                                                            option.fromNullable(
                                                                c.entry_fee_currency
                                                            ),
                                                    },
                                                    sequenceS(option.Apply),
                                                    option.fold(
                                                        constNull,
                                                        (ctx) => (
                                                            <>
                                                                <Typography
                                                                    sx={{
                                                                        ml: 2,
                                                                    }}
                                                                    component={
                                                                        "span"
                                                                    }
                                                                    variant={
                                                                        "body2"
                                                                    }
                                                                >
                                                                    {raceT.fee}:
                                                                </Typography>{" "}
                                                                <strong>
                                                                    <LocalCurrency
                                                                        value={
                                                                            ctx.fee
                                                                        }
                                                                        currency={
                                                                            ctx.currency
                                                                        }
                                                                    />
                                                                </strong>
                                                            </>
                                                        )
                                                    )
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ))
                        )}
                    </CardContent>
                </Card>
            ))}
            <SegmentsMapDialog state={segmentsToPreviewState} />
        </>
    );
};
